/**
 * Utility for loading external scripts dynamically
 */

import { RequestUtil } from '../common';

// Build-time injected by Rollup via @rollup/plugin-replace.
declare const process: { env: { NODE_ENV: string } };

// Cache to track loaded scripts
const loadedScripts = new Map<string, Promise<void>>();

/**
 * Mapping from real external script names to their local mock equivalents.
 * Used in development by default; can be forced/disabled at runtime via
 * `window.__WPP_USE_MOCK_SCRIPTS__` (boolean).
 */
const MOCK_SCRIPTS: Record<string, string> = {
    'initAddToAppleWallet.js': 'initAddToAppleWallet.mock.js',
    'integration.min.js': 'integration.mock.min.js',
};

/**
 * Window globals defined by each script. Cleared when switching between
 * real and mock so the newly loaded script truly takes over.
 */
const SCRIPT_GLOBALS: Record<string, string[]> = {
    'initAddToAppleWallet.js': ['initAddToAppleWallet'],
    'integration.min.js': ['googlepay'],
};

// Last loaded URL per logical scriptName, used to detect mode switches.
const lastLoadedUrl = new Map<string, string>();

function shouldUseMock(): boolean {
    const override = (globalThis as any)?.window?.__WPP_USE_MOCK_SCRIPTS__;
    if (typeof override === 'boolean') {
        return override;
    }
    return process.env.NODE_ENV !== 'production';
}

/**
 * Remove every previously-loaded variant (real + mock) of `scriptName` from
 * the DOM, the loader cache and the `window` globals it defined.
 */
function resetScript(scriptName: string): void {
    const candidates = [scriptName, MOCK_SCRIPTS[scriptName]].filter(Boolean) as string[];
    for (const name of candidates) {
        const url = RequestUtil.getResourceUrl('web-push-provisioning.js', `js/${name}`, 'data-wpp');
        loadedScripts.delete(url);
        document.querySelectorAll(`script[src="${url}"]`).forEach((s) => s.remove());
    }
    for (const key of SCRIPT_GLOBALS[scriptName] ?? []) {
        try {
            delete (window as any)[key];
        } catch {
            (window as any)[key] = undefined;
        }
    }
}

/**
 * Dynamically loads an external script and caches the promise
 * @param src The script URL to load
 * @param timeout Timeout in milliseconds (default: 10000ms)
 * @returns Promise that resolves when script is loaded
 */
export function loadScript(src: string, timeout: number = 10000): Promise<void> {
    // Check if already loaded or loading
    if (loadedScripts.has(src)) {
        return loadedScripts.get(src)!;
    }

    // Create the loading promise
    const promise = new Promise<void>((resolve, reject) => {
        // Check if script already exists in DOM
        const existingScript = document.querySelector(`script[src="${src}"]`);
        if (existingScript) {
            resolve();
            return;
        }

        // Create script element
        const script = document.createElement('script');
        script.src = src;
        script.type = 'text/javascript';
        script.async = true;

        // Setup timeout
        const timeoutId = setTimeout(() => {
            cleanup();
            reject(new Error(`Script load timeout: ${src}`));
        }, timeout);

        // Cleanup function
        const cleanup = () => {
            clearTimeout(timeoutId);
            script.removeEventListener('load', onLoad);
            script.removeEventListener('error', onError);
        };

        // Success handler
        const onLoad = () => {
            cleanup();
            resolve();
        };

        // Error handler
        const onError = () => {
            cleanup();
            // Remove failed script from DOM
            script.remove();
            // Remove from cache so it can be retried
            loadedScripts.delete(src);
            reject(new Error(`Failed to load script: ${src}`));
        };

        // Attach event listeners
        script.addEventListener('load', onLoad);
        script.addEventListener('error', onError);

        // Append to document
        document.head.appendChild(script);
    });

    // Cache the promise
    loadedScripts.set(src, promise);

    return promise;
}

/**
 * Loads a script by name from the same directory as web-push-provisioning.js.
 * In development (or when `window.__WPP_USE_MOCK_SCRIPTS__ === true`), the
 * real Apple/Google scripts are transparently swapped for their local mocks.
 *
 * If the effective URL differs from the one previously loaded for the same
 * logical `scriptName` (i.e. the caller toggled the mock flag), the previous
 * variant is unloaded so the new one can take over cleanly.
 */
export function loadScriptByName(scriptName: string): Promise<void> {
    const effectiveName = shouldUseMock() && MOCK_SCRIPTS[scriptName] ? MOCK_SCRIPTS[scriptName] : scriptName;
    const scriptUrl = RequestUtil.getResourceUrl('web-push-provisioning.js', `js/${effectiveName}`, 'data-wpp');

    const previousUrl = lastLoadedUrl.get(scriptName);
    if (previousUrl && previousUrl !== scriptUrl) {
        resetScript(scriptName);
    }
    lastLoadedUrl.set(scriptName, scriptUrl);

    return loadScript(scriptUrl);
}

/**
 * Check if a script is already loaded
 */
export function isScriptLoaded(src: string): boolean {
    return loadedScripts.has(src) || !!document.querySelector(`script[src="${src}"]`);
}
